/**
 * CoreDailyBrief — Bloomberg-style daily recap of the 3 AI core bots.
 * Auto-generated text + status lights per bot. Reads /api/ai-bots/daily-brief.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { TrendingUp, TrendingDown, MinusCircle, Newspaper, Clock } from 'lucide-react';
import { apiFetch } from '../utils/apiFetch';
import { BOT_CONFIG } from './MTModule';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS = {
  positive: { color: '#22c55e', glow: 'rgba(34,197,94,0.6)',  Icon: TrendingUp,    label: 'WINNING DAY' },
  negative: { color: '#ef4444', glow: 'rgba(239,68,68,0.6)',  Icon: TrendingDown,  label: 'LOSING DAY' },
  idle:     { color: '#9EB4C0', glow: 'rgba(158,180,192,0.4)',Icon: MinusCircle,   label: 'SAT OUT' },
  flat:     { color: '#fbbf24', glow: 'rgba(251,191,36,0.5)', Icon: MinusCircle,   label: 'FLAT' },
};

const fmtUsd = (n) => {
  if (n === null || n === undefined) return '$0';
  const sign = n >= 0 ? '+' : '-';
  const v = Math.abs(n);
  if (v >= 1000) return `${sign}$${(v / 1000).toFixed(1)}K`;
  return `${sign}$${v.toFixed(0)}`;
};

const sentence = (bot, cfg) => {
  const name = cfg?.label || bot.bot;
  if (bot.status === 'idle') return `${name} se quedó al margen esperando setup`;
  if (bot.trades === 0)      return `${name} no operó ayer`;
  const verb = bot.net_usd >= 0 ? 'cerró' : 'tomó pérdidas por';
  const sym = bot.top_symbol ? ` en ${bot.top_symbol}` : '';
  return `${name} ${verb} ${fmtUsd(bot.net_usd)}${sym} con ${bot.trades} trade${bot.trades === 1 ? '' : 's'} (${bot.won}W/${bot.lost}L)`;
};

const StatusOrb = ({ status, color, glow, animate }) => (
  <div
    className="w-3 h-3 rounded-full shrink-0"
    style={{
      background: color,
      boxShadow: `0 0 10px ${glow}, 0 0 20px ${glow}`,
      animation: animate ? 'sim360-orb-pulse 1.8s ease-in-out infinite' : 'none',
    }}
    data-testid={`status-orb-${status}`}
  />
);

const CoreDailyBrief = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch(`${API}/ai-bots/daily-brief`)
      .then((r) => r.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="border" style={{ background: 'rgba(4,8,22,0.6)', borderColor: 'rgba(0,244,255,0.18)' }}>
        <CardContent className="p-4 text-center text-[11px] text-[#9EB4C0]">Loading daily brief…</CardContent>
      </Card>
    );
  }

  const bots = data?.bots || [];
  if (bots.length === 0) {
    return (
      <Card className="border" style={{ background: 'rgba(4,8,22,0.6)', borderColor: 'rgba(0,244,255,0.18)' }}>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <Newspaper className="w-4 h-4 text-[#00f4ff]" />
            <h3 className="text-base font-bold text-white">Core Daily Brief</h3>
          </div>
          <p className="text-[11px] text-[#9EB4C0]">Awaiting first daily roll-up from the 3 Core AI accounts.</p>
        </CardContent>
      </Card>
    );
  }

  const combined = data.combined || { trades: 0, net_usd: 0, status: 'idle' };
  const combinedCfg = STATUS[combined.status] || STATUS.idle;

  return (
    <Card
      className="overflow-hidden border"
      style={{
        background: 'linear-gradient(180deg, rgba(0,244,255,0.05), rgba(4,8,22,0.85))',
        borderColor: 'rgba(0,244,255,0.30)',
        boxShadow: '0 18px 60px -25px rgba(0,244,255,0.35)',
      }}
      data-testid="core-daily-brief"
    >
      {/* Header — Bloomberg-style ticker bar */}
      <div className="px-5 py-2 flex items-center justify-between" style={{ background: 'rgba(0,244,255,0.06)', borderBottom: '1px solid rgba(0,244,255,0.18)' }}>
        <div className="flex items-center gap-2">
          <Newspaper className="w-4 h-4 text-[#00f4ff]" />
          <h3 className="text-sm font-bold tracking-[0.15em] uppercase text-white">Core Daily Brief</h3>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-[#9EB4C0] tracking-[0.2em] uppercase">
          <Clock className="w-3 h-3" /> {data.as_of}
        </div>
      </div>

      <CardContent className="p-5">
        {/* Combined headline */}
        <div className="flex items-center gap-3 mb-4 pb-4" style={{ borderBottom: '1px dashed rgba(0,244,255,0.18)' }}>
          <StatusOrb status={combined.status} color={combinedCfg.color} glow={combinedCfg.glow} animate />
          <div className="flex-1">
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#9EB4C0]">Trio combined</div>
            <div className="text-2xl font-black tabular-nums font-mono" style={{ color: combinedCfg.color, textShadow: `0 0 12px ${combinedCfg.glow}` }}>
              {fmtUsd(combined.net_usd)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#9EB4C0]">Total trades</div>
            <div className="text-base font-bold text-white tabular-nums">{combined.trades}</div>
          </div>
        </div>

        {/* Per-bot lines */}
        <div className="space-y-2.5">
          {bots.map((b) => {
            const cfg = BOT_CONFIG[b.bot] || { label: b.bot, color: '#9EB4C0' };
            const st = STATUS[b.status] || STATUS.idle;
            const StIcon = st.Icon;
            return (
              <div key={b.bot}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
                style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${cfg.color}22` }}
                data-testid={`brief-row-${b.bot}`}
              >
                <StatusOrb status={b.status} color={st.color} glow={st.glow} animate={b.status !== 'idle'} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-white leading-snug">{sentence(b, cfg)}</div>
                  <div className="text-[9px] tracking-[0.25em] uppercase mt-0.5" style={{ color: st.color }}>
                    {st.label}
                  </div>
                </div>
                <StIcon className="w-4 h-4 shrink-0" style={{ color: st.color }} />
              </div>
            );
          })}
        </div>

        {/* Footer disclosure */}
        <p className="mt-3 text-[9px] text-[#556475] italic tracking-wider text-center">
          Auto-generated from MetaApi historical trades · {data.as_of} UTC
        </p>
      </CardContent>

      <style>{`
        @keyframes sim360-orb-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.25); opacity: 0.75; }
        }
      `}</style>
    </Card>
  );
};

export default CoreDailyBrief;
